// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isDefined, isNull, isNullOrUndefined } from '@shared/utils';

dayjs.extend(customParseFormat);

export type DateFormat =
  | 'standard'
  | 'short'
  | 'long'
  | 'full'
  | 'medium'
  | 'year'
  | 'standard-dmy'
  | 'short-dmy'
  | 'month-year';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

const DATE_FORMAT_OPTIONS = Object.freeze({
  standard: Object.freeze({
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }),
  short: Object.freeze({
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }),
  'standard-dmy': Object.freeze({
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }),
  'short-dmy': Object.freeze({
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }),
  medium: Object.freeze({ dateStyle: 'medium' }),
  long: Object.freeze({ dateStyle: 'long' }),
  full: Object.freeze({ dateStyle: 'full' }),
  year: Object.freeze({ year: 'numeric' }),
  'month-year': Object.freeze({ month: 'long', year: 'numeric' }),
}) satisfies Record<DateFormat, Intl.DateTimeFormatOptions>;

export const NUMERIC_DATE_FORMAT_OPTIONS = Object.freeze({
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}) satisfies Intl.DateTimeFormatOptions;

export const getDateLocale = (lng?: string): string => {
  if (isNullOrUndefined(lng) || lng === '') {
    return 'en-US';
  }

  if (lng.includes('-')) return lng;

  const language = lng.toLowerCase();

  return language === 'es' ? 'es-ES' : 'en-US';
};

const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

export const parseDate = (value: string): Date | null => {
  const match = ISO_DATE_PREFIX.exec(value);

  if (!match) return null;

  const parsed = dayjs(match[1], ISO_DATE_FORMAT, true);

  return parsed.isValid() ? parsed.toDate() : null;
};

const toValidDate = (value: Date | string | number): Date | null => {
  if (value instanceof Date) return dayjs(value).isValid() ? value : null;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;

    const parsed = dayjs(value);

    return parsed.isValid() ? parsed.toDate() : null;
  }

  return parseDate(value);
};

const resolveFormatOptions = (
  format: DateFormat,
  overrideOptions?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions => overrideOptions ?? DATE_FORMAT_OPTIONS[format];

/**
 * Formats a date value with Intl.DateTimeFormat. Accepts ISO date strings, Date
 * instances, or millisecond timestamps. ISO strings are parsed as calendar dates;
 * timestamps and Date values keep their instant and format in the runtime timezone.
 * Returns nullish inputs unchanged; returns the original string when parsing
 * fails.
 */
export const formatDate = (
  value: Date | string | number | null | undefined,
  format: DateFormat = 'standard',
  language?: string,
  options?: Intl.DateTimeFormatOptions,
): string | null | undefined => {
  if (isNullOrUndefined(value)) return value;

  const date = toValidDate(value);

  if (isNull(date)) return typeof value === 'string' ? value : null;

  const locale = getDateLocale(language);

  return new Intl.DateTimeFormat(
    locale,
    resolveFormatOptions(format, options),
  ).format(date);
};
